/**
 * ████████╗ ██████╗ ██╗     ██╗███╗   ██╗ ██████╗
 * ╚══██╔══╝██╔═══██╗██║     ██║████╗  ██║██╔═══██╗
 *    ██║   ██║   ██║██║     ██║██╔██╗ ██║██║   ██║
 *    ██║   ██║   ██║██║     ██║██║╚██╗██║██║   ██║
 *    ██║   ╚██████╔╝███████╗██║██║ ╚████║╚██████╔╝
 *    ╚═╝    ╚═════╝ ╚══════╝╚═╝╚═╝  ╚═══╝ ╚═════╝
 *
 * (c) Copyright 2023-present Rakuten Kobo Inc. (https://www.kobo.com)
 */

// -----------------------------------------------------------------------------
// IMPORTS
// -----------------------------------------------------------------------------

// External/Third-party dependencies


// Internal dependencies
import { preventConcurrentExec } from "@rakuten/common-util/promise";
import { SessionError } from "./session.error";
import { TAG, DUMMY_COOKIE } from "./session.constants";


// -----------------------------------------------------------------------------
// SERVICE REGISTRATION
// -----------------------------------------------------------------------------

// Service config for service locator
export const config = ["session", ["logger", "localConfig"], createSessionService];


// -----------------------------------------------------------------------------
// HELPER
// -----------------------------------------------------------------------------

/**
 * Small helper to get a value for a key from the document.cookie
 *
 * @param {string} key Identifier of the session value to get
 * @returns {string|null} The value if available or null
 */
function getSessionValue(key) {
  let cookieItem = document.cookie
    .split('; ')
    .find(item => item.startsWith(`${key}=`));

  if (cookieItem) {
    const index = cookieItem.indexOf('=');
    return cookieItem.substring(index + 1);
  }

  return null;
}


// -----------------------------------------------------------------------------
// KOBO SESSION SERVICE
// -----------------------------------------------------------------------------

/**
 * Factory function which creates a service with given
 * dependencies
 *
 * The ReadingService will use cookies passed through in the headings
 * of the request to determine if the user making the request is
 * authenticated.  If a user is authenticated we will redirect the
 * user to the index page of the WebReader
 * (i.e. https://readingservices.kobo.com/Reading/{ProductId}).
 * If a user is authenticated ReadNow will make a request to the
 * WebReader at https://readingservices.kobo.com/Reading/{ProductId}.
 */
export function createSessionService(logger, localConfig) {
  const appConfig = localConfig.getLocalConfig();

  // For local development we cheat and add a cookie
  if (appConfig.BUNDLER.BUILD_MODE === appConfig.BUNDLER.BUILD_MODES.DEVELOPMENT) {
    document.cookie = `; ${DUMMY_COOKIE}`;
  }

  // Used to determine if session has changed
  let currentSessionID;

  // Prevent multiple calls to functions that create the same promise
  const guardedPromise = preventConcurrentExec();

  /**
   * Gets the sessionId from the cookie. It is basically synchronous
   * but I'am not sure if it should stay that way since other resellers
   * could introduce a different approach.
   *
   * @deprecated (use fetchSessionID)
   */
  function fetchSession() {
    return fetchSessionID();
  }


  function fetchSessionID() {
    return guardedPromise(`{TAG}:fetchSession`, () => {
      let sessionID;
      try {
        sessionID = getSessionValue('sessionId').split('=')[1];
        if (!sessionID) {
          return Promise.reject(new SessionError("MISSING_SESSION"));
        }
      } catch (e) {
        return Promise.reject(new SessionError("MISSING_SESSION"), {
          detail: {
            parent: e
          }
        });
      }

      if (sessionID !== currentSessionID) {
        logger.warn(`${TAG} Session ID has changed`);
        currentSessionID = sessionID;
      }

      return Promise.resolve(sessionID);
    });
  }


  function getIPCountry() {
    const session = getSessionValue('session');
    if (session) {
      const ipCountry = session.split('&').find(item => item.startsWith('ipCountry='));
      if (ipCountry) {
        return ipCountry.split('=')[1];
      }
    }

    return null;
  }


  // ---------------------------------------------------------------------------
  // PUBLIC API
  // ---------------------------------------------------------------------------

  return {
    fetchSession,
    fetchSessionID,
    getIPCountry
  };
}
